import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchLastMedicationOrder, searchDrugCatalog } from '../../../../../api/medication'
import { MedFrequencyResMock } from '../../../../../types/med_frequency.type'
import {
  AutoFilled,
  catalogDefaults,
  lastOrderDefaults,
  MedicationPrefill,
  onlyEmptyFields,
  PrefillTarget,
  recordAutoFilled,
  takeBackAutoFill
} from './medicationPrefill'

type Options = {
  /** Read what the order form holds right now. */
  getCurrent: () => PrefillTarget
  /** Write values into the order form (see MedicationPrefill for the meaning of each key). */
  apply: (prefill: MedicationPrefill) => void
  routes?: string[]
  medFrequencies?: MedFrequencyResMock[]
}

const hasKeys = (value: object) => Object.keys(value).length > 0

/**
 * Smart defaults for a new medication order. Call `onDrugChange` only when the prescriber picks a
 * drug (not on load/edit/reset): it fills the still-empty fields from their last order of that drug,
 * or else from the drug catalog. Failures are silent — the form never waits on this.
 */
export const useMedicationPrefill = (options: Options) => {
  const optionsRef = useRef(options)
  optionsRef.current = options
  const requestRef = useRef(0)
  const autoFilledRef = useRef<AutoFilled>({})
  const [note, setNote] = useState<string | null>(null)

  /** Forget any prefill (form was cleared or another order loaded) and drop answers in flight. */
  const reset = useCallback(() => {
    requestRef.current += 1
    autoFilledRef.current = {}
    setNote(null)
  }, [])

  // A reply that lands after the modal closed must not touch the (shared) form state.
  useEffect(
    () => () => {
      requestRef.current += 1
    },
    []
  )

  const onDrugChange = useCallback(async (drugName: string) => {
    const token = ++requestRef.current
    const isStale = () => token !== requestRef.current
    setNote(null)

    const takeBack = takeBackAutoFill(autoFilledRef.current, optionsRef.current.getCurrent())
    autoFilledRef.current = {}
    if (hasKeys(takeBack)) optionsRef.current.apply(takeBack)

    const name = (drugName || '').trim()
    if (!name) return

    let prefill: MedicationPrefill | null = null
    let orderedAt: string | null = null
    try {
      const last = await fetchLastMedicationOrder(name)
      if (isStale()) return
      if (last) {
        prefill = lastOrderDefaults(last.medication, last.included, optionsRef.current.medFrequencies)
        orderedAt = last.medication.attributes?.created_at || last.medication.attributes?.start_at || null
      }
    } catch (error) {
      if (isStale()) return
      console.warn('Medication prefill: last order lookup failed, using the drug catalog', error)
    }

    if (!prefill) {
      try {
        const form = optionsRef.current.getCurrent().form?.trim()
        const hits = await searchDrugCatalog(name, form || undefined)
        if (isStale()) return
        prefill = catalogDefaults(name, hits, optionsRef.current.routes)
      } catch (error) {
        if (!isStale()) console.warn('Medication prefill: drug catalog lookup failed', error)
        return
      }
    }
    if (!prefill) return

    const toApply = onlyEmptyFields(prefill, optionsRef.current.getCurrent())
    if (!hasKeys(toApply)) return
    optionsRef.current.apply(toApply)
    autoFilledRef.current = recordAutoFilled(toApply)
    if (orderedAt && moment(orderedAt).isValid()) {
      // The last order may have been for another patient: the prescriber must still check the dose.
      setNote(`Filled from your last order of this drug (${moment(orderedAt).format('D MMM YYYY')}) – check the dose`)
    }
  }, [])

  return { note, onDrugChange, reset }
}
